import React, { useEffect, useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Tooltip,
  LinearProgress,
  TablePagination,
  Stack,
} from '@mui/material';
import { formatDistanceToNow, format } from 'date-fns';
import { jobsService, type JobHistoryResponse, type JobHistoryRow } from '../services';
import { getApiErrorMessage } from '../lib/apiError';

function formatDuration(ms: number | null): string {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
  const m = Math.floor(ms / 60_000);
  const s = Math.round((ms % 60_000) / 1000);
  return `${m}m ${s}s`;
}

/**
 * Flatten a jsonb summary to "key: value" pairs. Recurses one level into
 * nested objects (e.g., DHIS2 sync has `{ recipients: { created, updated } }`)
 * — critical otherwise those rows read as "—" which looks like no work done.
 * Arrays and deeper nesting are intentionally not expanded.
 */
function summaryText(summary: unknown): string {
  if (!summary || typeof summary !== 'object') return '—';
  const parts: string[] = [];
  for (const [k, v] of Object.entries(summary as Record<string, unknown>)) {
    if (v == null) continue;
    if (typeof v !== 'object') {
      parts.push(`${k}: ${v}`);
    } else if (!Array.isArray(v)) {
      for (const [k2, v2] of Object.entries(v as Record<string, unknown>)) {
        if (v2 == null || typeof v2 === 'object') continue;
        parts.push(`${k}.${k2}: ${v2}`);
      }
    }
  }
  return parts.length === 0 ? '—' : parts.join(' · ');
}

function StatusChip({ status }: { status: JobHistoryRow['status'] }) {
  const map: Record<JobHistoryRow['status'], { label: string; color: 'success' | 'error' | 'warning' | 'info' }> = {
    success: { label: 'Success', color: 'success' },
    failed: { label: 'Failed', color: 'error' },
    skipped: { label: 'Skipped', color: 'warning' },
    running: { label: 'Running', color: 'info' },
  };
  const m = map[status];
  return <Chip size="small" label={m.label} color={m.color} />;
}

interface JobHistoryPanelProps {
  jobName: string;
}

export const JobHistoryPanel: React.FC<JobHistoryPanelProps> = ({ jobName }) => {
  const [data, setData] = useState<JobHistoryResponse | null>(null);
  const [page, setPage] = useState(0); // MUI pagination is 0-indexed; API is 1-indexed
  const [limit, setLimit] = useState(20);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await jobsService.getHistory(jobName, page + 1, limit);
        if (cancelled) return;
        // Clamp page if it's now past the end (e.g., rows aged out since
        // the user paginated). Without this, the UI renders "No runs" even
        // though `total > 0`.
        if (res.pagination.total > 0 && page + 1 > res.pagination.totalPages) {
          setPage(Math.max(0, res.pagination.totalPages - 1));
          return; // effect will re-run with the corrected page
        }
        setData(res);
      } catch (err: any) {
        if (!cancelled) setError(getApiErrorMessage(err, 'Failed to load history'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [jobName, page, limit]);

  return (
    <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
      {/* Summary stats */}
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        <Chip
          size="small"
          label={`${data?.stats.total ?? '—'} runs`}
          variant="outlined"
        />
        <Chip
          size="small"
          label={`${data?.stats.success ?? 0} success`}
          color="success"
          variant="outlined"
        />
        {(data?.stats.failed ?? 0) > 0 && (
          <Chip
            size="small"
            label={`${data?.stats.failed} failed`}
            color="error"
            variant="outlined"
          />
        )}
        {(data?.stats.skipped ?? 0) > 0 && (
          <Chip
            size="small"
            label={`${data?.stats.skipped} skipped`}
            color="warning"
            variant="outlined"
          />
        )}
        {(data?.stats.running ?? 0) > 0 && (
          <Chip
            size="small"
            label={`${data?.stats.running} running`}
            color="info"
            variant="outlined"
          />
        )}
        {data?.stats.avgMs != null && (
          <Chip size="small" label={`avg ${formatDuration(data.stats.avgMs)}`} variant="outlined" />
        )}
        {/* Hide p95 until we have a meaningful sample — nearest-rank at small
            N is statistically misleading and invites false confidence. */}
        {data?.stats.p95Ms != null && data.stats.total >= 10 && (
          <Chip size="small" label={`p95 ${formatDuration(data.stats.p95Ms)}`} variant="outlined" />
        )}
      </Stack>

      {loading && !data ? (
        <LinearProgress />
      ) : error ? (
        <Typography color="error" variant="body2">
          {error}
        </Typography>
      ) : !data || data.rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No runs recorded in the last 7 days.
        </Typography>
      ) : (
        <TableContainer sx={{ bgcolor: 'background.paper', borderRadius: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Started</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Summary</TableCell>
                <TableCell>Error</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.rows.map((r) => {
                const started = new Date(r.startedAt);
                return (
                  <TableRow key={r.id} hover>
                    <TableCell>
                      <Tooltip title={format(started, 'PPpp')}>
                        <Typography variant="body2" sx={{ cursor: 'help' }}>
                          {formatDistanceToNow(started, { addSuffix: true })}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <StatusChip status={r.status} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {r.status === 'running' ? 'running…' : formatDuration(r.durationMs)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          maxWidth: 320,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {summaryText(r.summary)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {r.errorMessage ? (
                        <Tooltip title={r.errorMessage}>
                          <Typography
                            variant="body2"
                            color="error"
                            sx={{
                              maxWidth: 240,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              cursor: 'help',
                            }}
                          >
                            {r.errorMessage}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={data.pagination.total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={limit}
            onRowsPerPageChange={(e) => {
              setLimit(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50, 100]}
          />
        </TableContainer>
      )}
    </Box>
  );
};
